import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Zap, X, Trash2, CheckSquare, Square } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, Eyebrow, Spinner } from "@/components/tac";
import { TRADES } from "@/lib/trades";

const UNDO_MS = 5000;

export default function Jobs() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [pending, setPending] = useState(new Set()); // ids optimistically hidden pending delete
  const timers = useRef({});
  const canManage = ["owner", "dispatcher"].includes(user?.role);

  const { data: jobs, isLoading } = useQuery({ queryKey: ["jobs"], queryFn: async () => (await api.get("/jobs")).data });
  const { data: customers } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data, enabled: canManage });

  const [f, setF] = useState({ title: "", customer_id: "", trade: "av_low_voltage", description: "" });
  const create = async () => {
    try {
      await api.post("/jobs", f);
      toast.success("Job created");
      setOpen(false); setF({ title: "", customer_id: "", trade: "av_low_voltage", description: "" });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const refresh = () => { qc.invalidateQueries({ queryKey: ["jobs"] }); qc.invalidateQueries({ queryKey: ["overview"] }); };
  const hide = (ids) => setPending((p) => new Set([...p, ...ids]));
  const unhide = (ids) => setPending((p) => { const n = new Set(p); ids.forEach((i) => n.delete(i)); return n; });

  // Optimistically hide, then commit the delete after a short undo window.
  const scheduleDelete = (ids, commit, label) => {
    hide(ids);
    const key = ids.join(",");
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(async () => {
      delete timers.current[key];
      try { await commit(); refresh(); }
      catch (e) { unhide(ids); toast.error(apiErr(e.response?.data?.detail)); }
    }, UNDO_MS);
    toast(label, {
      duration: UNDO_MS,
      action: {
        label: "Undo",
        onClick: () => { clearTimeout(timers.current[key]); delete timers.current[key]; unhide(ids); toast.success("Restored"); },
      },
    });
  };

  const deleteOne = (job) =>
    scheduleDelete([job.id], () => api.delete(`/jobs/${job.id}`), `Deleted "${job.title}"`);

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };
  const bulkDelete = () => {
    if (!selected.size) return;
    const ids = [...selected];
    scheduleDelete(ids, () => api.post("/jobs/bulk-delete", { ids }), `Deleted ${ids.length} job(s)`);
    exitSelect();
  };

  const visible = (jobs || []).filter((j) => !pending.has(j.id));

  return (
    <div className="mx-auto max-w-[1400px] p-6">
      <div className="flex items-center justify-between">
        <div>
          <Eyebrow>Operations</Eyebrow>
          <h1 className="mt-2 font-display text-3xl font-bold text-white">Jobs</h1>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {selectMode ? (
              <>
                <button data-testid="jobs-bulk-delete-btn" onClick={bulkDelete} disabled={!selected.size}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-500/50 bg-rose-950/40 px-4 py-2.5 text-sm text-rose-300 hover:bg-rose-900/50 disabled:opacity-40">
                  <Trash2 className="h-4 w-4" /> Delete ({selected.size})
                </button>
                <button data-testid="jobs-select-cancel-btn" onClick={exitSelect}
                  className="rounded-full border border-slate-700 px-4 py-2.5 text-sm text-slate-300 hover:text-white">Cancel</button>
              </>
            ) : (
              <>
                <button data-testid="jobs-select-btn" onClick={() => setSelectMode(true)}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2.5 text-sm text-slate-300 hover:text-white">
                  <CheckSquare className="h-4 w-4" /> Select
                </button>
                <button data-testid="new-job-btn" onClick={() => setOpen(true)} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm">
                  <Plus className="h-4 w-4" /> New job
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {isLoading ? <div className="mt-10"><Spinner /></div> : (
        <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((j) => {
            const isSel = selected.has(j.id);
            const go = (e) => { if (selectMode) { e.preventDefault(); toggle(j.id); } };
            return (
              <Link key={j.id} to={`/jobs/${j.id}`} onClick={go} data-testid={`job-card-${j.id}`}
                className={`card-tac relative p-5 transition-colors ${isSel ? "ring-2 ring-rose-500/70" : ""}`}>
                <div className="flex items-start justify-between">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-500/10 text-amber-400"><Zap className="h-5 w-5" /></div>
                  <div className="flex items-center gap-2">
                    <StatusPill status={j.status} />
                    {selectMode ? (
                      <span className="text-slate-300" data-testid={`job-select-${j.id}`}>
                        {isSel ? <CheckSquare className="h-5 w-5 text-rose-400" /> : <Square className="h-5 w-5" />}
                      </span>
                    ) : canManage && (
                      <button data-testid={`job-delete-${j.id}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteOne(j); }}
                        className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-rose-950/50 hover:text-rose-400" title="Delete job">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <h3 className="mt-4 font-display text-base font-semibold text-white">{j.title}</h3>
                <p className="mt-1 text-sm text-slate-400">{j.customer_name}</p>
                <p className="mt-3 font-mono-tac text-xs text-slate-600">{j.property_address}</p>
              </Link>
            );
          })}
          {!visible.length && <p className="text-sm text-slate-500">No jobs assigned.</p>}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div className="card-tac w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-white">New job</h2>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 space-y-3">
              <input data-testid="job-title-input" placeholder="Job title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                className="w-full rounded-xl border border-slate-700 bg-[#0d1420] px-4 py-3 text-sm text-white focus:border-amber-500 focus:outline-none" />
              <select data-testid="job-customer-select" value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value })}
                className="w-full rounded-xl border border-slate-700 bg-[#0d1420] px-4 py-3 text-sm text-white focus:border-amber-500 focus:outline-none">
                <option value="">Select customer…</option>
                {(customers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select data-testid="job-trade-select" value={f.trade} onChange={(e) => setF({ ...f, trade: e.target.value })}
                className="w-full rounded-xl border border-slate-700 bg-[#0d1420] px-4 py-3 text-sm text-white focus:border-amber-500 focus:outline-none">
                {TRADES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <textarea data-testid="job-desc-input" placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                className="w-full rounded-xl border border-slate-700 bg-[#0d1420] px-4 py-3 text-sm text-white focus:border-amber-500 focus:outline-none" rows={3} />
              <button data-testid="job-create-submit" disabled={!f.title || !f.customer_id} onClick={create} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">Create job</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
